// TA-10892 #32: the "Got crew for this job" banner names the task assignees
// who haven't joined the project yet. The list lives in localStorage
// ('non_member_assignee_emails'); this reconciles it with what the loaded
// project tasks and current membership say.

export type NonMemberAssignee = {
  email: string;
  id?: string;
  name?: string;
  avatarId?: string;
};

type Role = string | null | undefined;

type TaskLike = {
  invitations?:
    | ({
        email?: string | null;
        role?: Role;
        expiredAt?: string | null;
      } | null)[]
    | null;
  members?:
    | ({
        role?: Role;
        user?: {
          id?: string | null;
          email?: string | null;
          name?: string | null;
          avatar?: { id?: string | null } | null;
        } | null;
      } | null)[]
    | null;
};

type MemberLike = {
  user?: { id?: string | null; email?: string | null } | null;
} | null;

/**
 * Stored unjoined assignees, minus anyone who has since joined, plus any
 * unjoined assignee found on the loaded tasks (email invitations that
 * haven't expired, and assigned users outside the project).
 */
export const reconcileNonMemberAssignees = ({
  stored,
  tasks,
  projectMembers,
  assigneeRole,
  now = Date.now(),
}: {
  stored: NonMemberAssignee[];
  tasks: TaskLike[];
  projectMembers: MemberLike[];
  assigneeRole: string;
  now?: number;
}): NonMemberAssignee[] => {
  const memberIds = new Set<string>();
  const memberEmails = new Set<string>();
  projectMembers.forEach((m) => {
    if (m?.user?.id) memberIds.add(m.user.id);
    if (m?.user?.email) memberEmails.add(m.user.email.toLowerCase());
  });
  const hasJoined = (a: { email: string; id?: string }) =>
    (!!a.id && memberIds.has(a.id)) || memberEmails.has(a.email);

  const result = new Map<string, NonMemberAssignee>();
  stored.forEach((a) => {
    if (typeof a?.email !== 'string' || !a.email) return;
    const entry = { ...a, email: a.email.toLowerCase() };
    if (!hasJoined(entry)) result.set(entry.email, entry);
  });

  tasks.forEach((task) => {
    task.invitations?.forEach((inv) => {
      const email = inv?.email?.toLowerCase();
      const expired =
        !!inv?.expiredAt && new Date(inv.expiredAt).getTime() < now;
      if (!email || inv?.role !== assigneeRole || expired) return;
      if (!result.has(email) && !hasJoined({ email }))
        result.set(email, { email });
    });
    task.members?.forEach((member) => {
      const user = member?.user;
      const email = user?.email?.toLowerCase();
      if (member?.role !== assigneeRole || !user?.id || !email) return;
      if (hasJoined({ email, id: user.id })) return;
      result.set(email, {
        email,
        id: user.id,
        name: user.name ?? undefined,
        avatarId: user.avatar?.id ?? undefined,
      });
    });
  });

  return [...result.values()];
};
